import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { Card, Empty, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

/**
 * ⚡ PERFORMANCE OPTIMIZATION: Memoized DataCard with skeleton loading
 */

const StyledCard = styled(Card)`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  height: 100%;
  overflow: hidden;
  transition: all 0.3s ease;

  /* Layout only: the title face (Lora + ink) is AntdOverrides', the edge and
     any hover come from the Card tokens — the accent is never spent on a hover */
  .ant-card-head {
    min-height: 48px;
  }

  .ant-card-head-title {
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  }

  .ant-card-body {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

// ⚡ Skeleton animation for smooth loading state
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.secondary} 0%,
    ${({ theme }) => theme.colors.surface} 50%,
    ${({ theme }) => theme.colors.secondary} 100%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

const SkeletonText = styled(SkeletonBase)`
  height: ${({ $height }) => $height || '16px'};
  width: ${({ $width }) => $width || '100%'};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: ${({ $minHeight }) => $minHeight || '200px'};
  padding: ${({ theme }) => theme.spacing.sm} 0;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: ${({ $minHeight }) => $minHeight || '200px'};
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const RefreshButton = styled(Button)`
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

/**
 * ⚡ Memoized DataCard component - prevents unnecessary re-renders
 */
const DataCard = memo(({
  title,
  icon,
  loading = false,
  error = null,
  empty = false,
  emptyText = 'No data available',
  minHeight = '200px',
  skeletonRows = 4,
  onRefresh,
  extra,
  children,
  className,
}) => {
  const IconComponent = icon;

  const renderTitle = () => (
    <span>
      {IconComponent && <IconComponent style={{ marginRight: 8, color: 'var(--odin-accent)' }} />}
      {title}
    </span>
  );

  const renderExtra = () => (
    <>
      {extra}
      {onRefresh && (
        <RefreshButton
          type="text"
          icon={<ReloadOutlined spin={loading} />}
          onClick={onRefresh}
          disabled={loading}
          size="small"
        />
      )}
    </>
  );

  const renderContent = () => {
    if (loading) {
      // ⚡ Use skeleton loading instead of spinner for better perceived performance
      return (
        <LoadingWrapper $minHeight={minHeight}>
          {Array.from({ length: skeletonRows }).map((_, i) => (
            <SkeletonText
              key={i}
              $width={`${70 + Math.random() * 30}%`}
              $height="16px"
            />
          ))}
        </LoadingWrapper>
      );
    }

    if (error) {
      return (
        <ErrorWrapper $minHeight={minHeight}>
          <ErrorMessage>Failed to load data</ErrorMessage>
          <div>{error.message || 'An error occurred'}</div>
          {onRefresh && (
            <Button type="primary" onClick={onRefresh} icon={<ReloadOutlined />}>
              Retry
            </Button>
          )}
        </ErrorWrapper>
      );
    }

    if (empty) {
      return (
        <LoadingWrapper $minHeight={minHeight}>
          <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </LoadingWrapper>
      );
    }

    return children;
  };

  return (
    <StyledCard
      title={renderTitle()}
      extra={renderExtra()}
      className={className}
    >
      {renderContent()}
    </StyledCard>
  );
});

DataCard.displayName = 'DataCard';

DataCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  loading: PropTypes.bool,
  error: PropTypes.object,
  empty: PropTypes.bool,
  emptyText: PropTypes.string,
  minHeight: PropTypes.string,
  skeletonRows: PropTypes.number,
  onRefresh: PropTypes.func,
  extra: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default DataCard;
